import React, { useCallback, useContext, useState } from 'react';
import BaseMappingTable from './BaseMappingTable';
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import { validateAttributeToggle } from '../Utils/attributeValidation';
import { getMappingDropdownOptions } from '../Services/MappingServices';
import { mergeMappedOptions } from '../Utils/mergeMappedOptions';
import CreateAttributeModal from '../../CatalogManagement/modals/CreateAttributeModal';

const AttributeMappingTable = () => {
  const {
    channelKey,
    updateAttribute,
    attributeRowActions,
    setAttributeRowActions,
  } = useContext<MappingContextType>(MappingContext);

  // Row whose dropdown triggered "Create" — so we can apply the new attribute
  // name back to that row's mapped_attribute when the popup succeeds.
  const [createForRow, setCreateForRow] = useState<MappingAttribute | null>(null);

  // Names created in this session via the popup. We keep them around so they
  // appear in the dropdown list on re-open (the backend's dropdown-options
  // endpoint won't return them until the catalog side has indexed the new
  // attribute, which can lag).
  const [locallyCreated, setLocallyCreated] = useState<string[]>([]);

  const loadMappedOptions = useCallback(
    async (search: string, page: number) => {
      if (!channelKey) return { options: [], hasMore: false };
      const res = await getMappingDropdownOptions(channelKey, undefined, page, 40, search);
      const rawList: { attribute?: string; mapped_attribute?: string }[] = res?.data?.attributes || [];
      const list: string[] = rawList
        .map((attr) => attr?.mapped_attribute || attr?.attribute || '')
        .filter(Boolean);

      // Prepend locally-created names so items the user just created this
      // session show up at the top of the dropdown even before the backend
      // has indexed the new Neo4j node.
      const merged = mergeMappedOptions(locallyCreated, list, search);

      return {
        options: merged.map((name) => ({ value: name, label: name })),
        hasMore: res?.data?.has_more ?? false,
      };
    },
    [channelKey, locallyCreated],
  );

  return (
    <>
      <BaseMappingTable<MappingAttribute>
        fields={{
          incoming: 'attribute',
          // Phase 2: persisted shape is `mapped_attributes: string[]` for
          // 1-to-many fan-out at the attribute level.
          mapped: 'mapped_attributes',
          considered: 'isConsidered',
          reviewed: 'isReviewed',
        }}
        labels={{
          incoming: 'Incoming Attribute',
          mapped: 'Mapped Attributes',
        }}
        onUpdate={updateAttribute}
        rowActions={attributeRowActions}
        setRowActions={setAttributeRowActions}
        validateToggle={(item, allCategories) => {
          const attributeName = item.attribute || (item.mapped_attributes?.[0] ?? '');
          return validateAttributeToggle(attributeName, allCategories);
        }}
        loadMappedOptions={loadMappedOptions}
        onCreateMapped={(item) => setCreateForRow(item)}
        testIdPrefix="attribute"
      />

      {createForRow && (
        <CreateAttributeModal
          isOpen
          onClose={() => setCreateForRow(null)}
          onCreate={(name) => {
            // Phase 2: append the just-created name to the row's
            // `mapped_attributes` list (multi-select), preserving any
            // existing selections.
            const existing = createForRow.mapped_attributes || [];
            const next = existing.includes(name) ? existing : [...existing, name];
            updateAttribute(createForRow.attribute, { mapped_attributes: next });
            setLocallyCreated((prev) => (prev.includes(name) ? prev : [name, ...prev]));
            setCreateForRow(null);
          }}
          // No product/category context on the Mapping tab — backend skips the
          // Category→Attribute Neo4j edge when category_id is empty.
          csKey={channelKey}
          showInfoBar={false}
        />
      )}
    </>
  );
};

export default AttributeMappingTable;
